import { useEffect, useMemo, useState } from "react";

// Bootstrap-aligned breakpoints. A column with `hideBelow: "<key>"` is removed
// from the rendered table whenever the matching media query is active.
//
// We resolve responsive hiding in JS (filtering columns out of the colgroup
// and every row) rather than via CSS `display: none` on cells. Hiding a
// `<th>`/`<td>` with CSS shifts the cell-to-`<col>` index mapping for the
// rest of the row: the cell that used to map to col N now maps to col N-1,
// which means a right-aligned Actions column inherits a collapsed col's
// 0 width and visually disappears. Removing the column from the data
// instead keeps the mapping stable.
export const RESPONSIVE_HIDE_QUERIES = {
  sm: "(max-width: 575.98px)",
  md: "(max-width: 767.98px)",
  lg: "(max-width: 991.98px)",
  xl: "(max-width: 1199.98px)",
  xxl: "(max-width: 1399.98px)",
};

export const useActiveHideBreakpoints = () => {
  const getInitial = () => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return {};
    }
    const initial = {};
    for (const [key, query] of Object.entries(RESPONSIVE_HIDE_QUERIES)) {
      initial[key] = window.matchMedia(query).matches;
    }
    return initial;
  };

  const [active, setActive] = useState(getInitial);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }
    const cleanups = Object.entries(RESPONSIVE_HIDE_QUERIES).map(([key, query]) => {
      const mql = window.matchMedia(query);
      const handler = (event) => {
        setActive((prev) => {
          if (prev[key] === event.matches) return prev;
          return { ...prev, [key]: event.matches };
        });
      };
      if (typeof mql.addEventListener === "function") {
        mql.addEventListener("change", handler);
        return () => mql.removeEventListener("change", handler);
      }
      mql.addListener(handler);
      return () => mql.removeListener(handler);
    });
    return () => cleanups.forEach((cleanup) => cleanup());
  }, []);

  return active;
};

export const useVisibleColumns = (columns) => {
  const activeBreakpoints = useActiveHideBreakpoints();
  return useMemo(
    () =>
      columns.filter(
        (col) => !col.hideBelow || !activeBreakpoints[col.hideBelow],
      ),
    [columns, activeBreakpoints],
  );
};
